import { generatePath } from 'react-router-dom';
import { ApiCacheTags, baseApiService } from '@qapline/api';
import { DEFAULT_CONFIG } from '@qapline/config';
import { z } from 'zod';

import { extendedCommissionSchema, TExtendedCommission } from '../schemas';
import {
    TCommissionsSummary,
    TExportCommissionsCSVRequestQuery,
    TGetCommissionsSummaryRequestQuery,
    TListCommissionsRequestQuery,
    TUpdateCommissionRequestBody,
} from '../types';

const commissionEndpoints = DEFAULT_CONFIG.api.endpoints.commission;
const commissionCacheTags = ApiCacheTags.commission;

export const commissionsApi = baseApiService.injectEndpoints({
    endpoints: (build) => ({
        getCommission: build.query<TExtendedCommission, { commissionId: number }>({
            query: ({ commissionId }) => ({
                url: generatePath(commissionEndpoints.get, { commissionId: String(commissionId) }),
                method: 'GET',
            }),
            providesTags: (_res, error, { commissionId }) =>
                error ? [] : [{ type: commissionCacheTags.item, id: commissionId }],
            transformResponse: (response) => extendedCommissionSchema.parse(response),
        }),

        listCommissions: build.query<{ data: TExtendedCommission[]; count: number }, TListCommissionsRequestQuery>({
            query: ({ filters, order, search, skip, take }) => ({
                url: commissionEndpoints.list,
                method: 'GET',
                params: {
                    filters: filters ? JSON.stringify(filters) : undefined,
                    order: order ? JSON.stringify(order) : undefined,
                    search,
                    skip,
                    take,
                },
            }),
            providesTags: (_res, error, req) =>
                error ? [] : [{ type: commissionCacheTags.list, id: JSON.stringify(req) }],
            transformResponse: (response) =>
                z.object({ data: z.array(extendedCommissionSchema), count: z.number() }).parse(response),
        }),

        updateCommission: build.mutation<
            TExtendedCommission,
            { commissionId: number; body: TUpdateCommissionRequestBody }
        >({
            query: ({ commissionId, body }) => ({
                url: generatePath(commissionEndpoints.update, { commissionId: String(commissionId) }),
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_res, error, { commissionId }) =>
                error
                    ? []
                    : [
                          commissionCacheTags.list,
                          { type: commissionCacheTags.item, id: commissionId },
                          commissionCacheTags.summary,
                      ],
            transformResponse: (response) => extendedCommissionSchema.parse(response),
        }),

        getCommissionsSummary: build.query<TCommissionsSummary, TGetCommissionsSummaryRequestQuery>({
            query: ({ filters, search }) => ({
                url: commissionEndpoints.summary,
                method: 'GET',
                params: {
                    filters: filters ? JSON.stringify(filters) : undefined,
                    search,
                },
            }),
            providesTags: (_res, error, req) =>
                error ? [] : [{ type: commissionCacheTags.summary, id: JSON.stringify(req) }],
            transformResponse: (response) =>
                z
                    .object({
                        totalPriceCustomer: z.number(),
                        totalPriceCarrier: z.number(),
                        totalProvision: z.number(),
                    })
                    .parse(response),
        }),
    }),
});

export const {
    useListCommissionsQuery,
    useLazyListCommissionsQuery,
    useGetCommissionQuery,
    useLazyGetCommissionQuery,
    useUpdateCommissionMutation,
    useGetCommissionsSummaryQuery,
} = commissionsApi;

export const getExportCommissionsCSVUrl = ({ filters, search, ids }: TExportCommissionsCSVRequestQuery) => {
    const params = new URLSearchParams();
    if (filters) params.set('filters', JSON.stringify(filters));
    if (search) params.set('search', search);
    if (ids && ids.length > 0) params.set('ids', ids.join(','));
    const query = params.toString();
    return `${commissionEndpoints.exportCsv}${query ? `?${query}` : ''}`;
};
